import {
  Box,
  Button,
  Text,
  Card,
  HStack,
  Flex,
  Heading,
  chakra,
  Badge,
  Divider,
  GridItem,
  Grid,
} from "@chakra-ui/react";
import DataTableV2 from "components/DataTable/DateTableV2";
import React, { useState, useEffect, useCallback } from "react";
import { isTaskClosed, taskService } from "api/ai/ai.service";
import { useQuery } from "react-query";
import { formatDate } from "utils";
import { prettyPrintCodeName, toTitleCase } from "utils/text-utils";
import { H2, H2Dashboard } from "components/text/headings";
import userAPI, { getUserName } from "api/user-administration/user-administration";
import { ResponsiveBar } from "@nivo/bar";
import fileinfoService from "api/common/fileinfo.service";
import { KB_FOLDER } from "api/common/fileinfo";
import { apiGetAISource } from "api/ai/ai.service";
import { DashboardNumber } from "../RequirementsCard";
import { setGlobal } from "context/global";
import { colors } from "theme/foundations/colors";
import { STATUSFIELD_VALUE_LABEL } from "../../../IssbDataInput/components/AdditionalStatuses";
import LocalSpinner from "components/LocalSpinner";
import _ from "lodash";
import { useSelector } from "react-redux";
import PieDialWithTotal from "../PieDialWithTotal";
import DashboardCard from "../DashboardCard";
import { routes } from "config/paths";
import { useNavigate } from "react-router-dom";
import assert from "utils/assert";
import { ResponsiveWaffle } from "@nivo/waffle";
import { H3 } from "components/text/headings";
import { MagicWand } from "@phosphor-icons/react";
import { nivo_theme } from "../chart-utils";
import HighMediumLow from "api/common/HighMediumLow";
/**
 * Results - by run (aka Task)
 * Figma: https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=1670-70162&p=f&t=fgPSqli7EHj2tMo9-0
 *
 * See also: AITasksListPanel.jsx
 */
export default function AIDashboard() {
  const [userSelectedTask, setUserSelectedTask] = useState(null);

  const roundSelector = useSelector((state) => state.issbRound);
  const questionnaireId = roundSelector.selectedRound?.id;

  // Get the list of tasks
  const { data: aiTasks, isLoading } = useQuery(
    "taskService.list",
    () => taskService.list({ round_id: questionnaireId }),
    {
      refetchInterval: 5000,
      cacheTime: 0,
      staleTime: 0
    }
  );

  /**
   * if there is running, show that one
   * if there is no running, show the lastest created date
   *
   * @param aiTasks
   */
  const findLatestTask = (aiTasks) => {
    // console.log("findLatestTask", aiTasks);
    if (!aiTasks || aiTasks.length === 0) {
      return null; // Return null if no tasks are available
    }

    // Check for a running task
    const runningTask = aiTasks.find((task) => task.status === "running");
    if (runningTask) {
      return runningTask; // Return the running task if found
    }
    const queuedTask = aiTasks.find((task) => task.status === "queued");
    if (queuedTask) {
      return queuedTask; // Return the queued task if found
    }

    // Find the task with the latest created date
    return aiTasks.sort((a, b) => new Date(b.created_date) - new Date(a.created_date))[0];
  };
  const selectedTask = userSelectedTask || findLatestTask(aiTasks);

  // get user (disguised async)
  // TODO move this into the user api code as a cache??
  const [user4id, setUser4id] = useState({});
  const promise4id = {};
  const _getUserById = (userId) => {
    if (user4id[userId]) {
      return user4id[userId];
    }
    if (!promise4id[userId]) {
      promise4id[userId] = userAPI.getUserById(userId).then((user) => {
        setUser4id({ ...user4id, [userId]: user });
      });
    }
    return { id: userId }; // return a stub while we wait for the real user
  };
  // skip the sources update tasks
  const _aiTasks = aiTasks?.filter((task) => task.task_type !== "sources_update");
  // add in user name
  const aiTasksWithUser = _aiTasks?.map((task) => ({
    ...task,
    user: _getUserById(task.user_id),
  }));
  // sort by created_date, most recent first
  aiTasksWithUser?.sort((a, b) => new Date(b.created_date) - new Date(a.created_date));

  if (isLoading) {
    return <LocalSpinner />;
  }
  // TODO: add in the sources
  const columns = [
    {
      header: "Date",
      accessorFn: (task) => formatDate(task.created_date),
    },
    {
      header: "Run by",
      accessorFn: (task) => getUserName(task.user),
    },
    {
      header: "Requirements",
      accessorFn: (task) => (task.task_type === "question_answer" ? task.inputs?.question_ids?.length : "-"),
    },
    // {
    //   header: "Sources",
    //   accessorFn: (task) => (task.task_type === "sources_update" ? task.inputs?.fileinfo_ids?.length : "-"),
    // },
    {
      header: "Status",
      accessorFn: (task) => toTitleCase(task.status),
    },
  ];
  // selected rows (Note: set each render to avoid being cleared by a query refresh)
  if (selectedTask) {
    aiTasksWithUser
      .filter((task) => task.id === selectedTask.id)
      .forEach((task) => {
        task.rowCustomStyles = {
          backgroundColor: "#FFFFFF",
        };
      });
  }

  return (
    <Flex direction="column" gap={4}>
      <AITaskDetailsPanel task={selectedTask} aiTasks={aiTasks} />
      <H2Dashboard>AI Reviews</H2Dashboard>
      <DataTableV2
        data={aiTasksWithUser?.slice(0, 10)}
        columns={columns}
        onRowClick={(event, row) => {
          setUserSelectedTask(row.original);
        }}
        queryPageSize={5} // not working, March 2025
        showPaginator={false}
      />
    </Flex>
  );
}

/**
 *
 * @param {*} navigate
 * @param {string[]?} questionIds
 * @param {string?} label
 * @param {URLSearchParams?} searchParams
 */
export function gotoFilteredQuestionList(navigate, questionIds, label = "AI Review", searchParams) {
  assert(
    questionIds === null || Array.isArray(questionIds),
    "questionIds must be an array",
    navigate,
    questionIds,
    label
  );
  const newParams = new URLSearchParams(searchParams);
  newParams.set("rtab", 0); // HACK: goes to the DataInputPage to show the filtered list
  // HACK: pass the list of question ids to the DataInputPage via a global variable, as it may be too large for a url-param
  if (questionIds) {
    setGlobal("questionIdsFilter", { label, ids: questionIds });
    newParams.set("q", "questionIdsFilter:true");
  }
  navigate(routes.issbRequirements + "?" + newParams.toString());
}

function AITaskDetailsPanel({ task, aiTasks }) {
  //   console.log("AITaskDetailsPanel", task);
  const [freshTask, setFreshTask] = useState();
  useEffect(() => {
    setFreshTask(task);
  }, [task]);
  useQuery({
    queryKey: ["ai-task", task?.id],
    queryFn: () => taskService.get(task.id),
    onSuccess: (data) => {
      setFreshTask(data);
    },
    refetchInterval: 2000,
    // If the task is done, failed or cancelled, then no point querying for updates.
    enabled: !!task && !isTaskClosed(freshTask),
  });

  // HACK: this links with the data stashed by the Python backend. It is all a bit adhoc.
  // const inputSources = task?.inputs?.fileinfo_ids?.length;
  const inputQuestions = freshTask?.inputs?.question_ids?.length;
  const outputs = freshTask?.outputs && Object.keys(freshTask?.outputs).length;
  const errorIds = Object.keys(freshTask?.outputs || {}).filter((key) => freshTask?.outputs[key]?.error);
  // Note: outputs includes errors (so we subtract that in the pie chart data below)
  // no-answer if we have an output (ie its not queued) but completeness is unset or none
  let noAnswerIds = Object.keys(freshTask?.outputs || {}).filter(
    (key) =>
      !freshTask?.outputs[key]?.json_content?.completeness ||
      freshTask.outputs[key].json_content.completeness === "NONE"
  );
  noAnswerIds = noAnswerIds.filter((key) => !errorIds.includes(key));
  // answer ids -- the succesful-ish ones
  let answerIds = Object.keys(freshTask?.outputs || {}).filter(
    (key) => freshTask?.outputs[key]?.json_content?.completeness
  );
  answerIds = answerIds.filter((key) => !noAnswerIds.includes(key) && !errorIds.includes(key));

  // Source info
  // HACK: this is from outside the AI Task itself -- because source processing is a sperate task, usually kicked off as they're uploaded.
  const [sourcesFailed, setSourcesFailed] = useState([]);
  // Slow, so cache via useQuery
  const { data: sourceInfo, isLoading: isSourceInfoLoading } = useQuery({
    queryKey: ["fetchSourceInfo", freshTask?.id], // Must re-run if the task changes
    queryFn: () => fetchSourceInfo({ task: freshTask, aiTasks }),
    onSuccess: (data) => {
      // now check for failed sources (meanwhile we can render the rest)
      fetchSourceInfo_FailedSources(sourceInfo?.sourcesProcessed).then(setSourcesFailed);
    },
    enabled: !!freshTask,
    refetchInterval: 20000, // 20 second refresh
  });
  const analysedCnt = sourceInfo?.sourcesProcessed?.length - (sourcesFailed?.length || 0);
  // get the sources used by this task
  let sourcesUsed;
  if (task?.task_type === "sources_update") {
    // Note: currently these tasks are filtered out
    sourcesUsed = task?.inputs?.fileinfo_ids;
  } else if (freshTask?.outputs) {
    // sources from the outputs?
    // Note: output.json_content.sources is the page-level slice-IDs
    sourcesUsed = Object.values(freshTask.outputs).map((output) => output.json_content?.files);
  }
  sourcesUsed = sourcesUsed && [...new Set(_.flatten(sourcesUsed).filter((x) => x))];
  const sourcesPieData = [
    { id: "analysed", value: analysedCnt, color: colors.aiTaskStatus.done },
    {
      id: "unanalysed",
      // Note: avoid NaN if any of the values are undefined
      value:
        (sourceInfo?.sources?.length || 0) -
        (analysedCnt || 0) -
        (sourceInfo?.sourcesQueued?.length || 0) -
        (sourcesFailed?.length || 0),
    },
    { id: "queued", value: sourceInfo?.sourcesQueued?.length, color: colors.aiTaskStatus.queued },
    { id: "error", value: sourcesFailed?.length, color: colors.aiTaskStatus.failed },
  ];
  const sourcesPieOnClick = datum => {
	console.log(datum);
	console.log("unanalysed", sourceInfo?.unanalysed);
	console.log("sourcesQueued", sourceInfo?.sourcesQueued);
	console.log("sourcesFailed", sourcesFailed);
  };

  //   // completeness - maybe not needed?
  //   const qids4completeness = {};
  //   Object.entries(freshTask?.outputs || {}).forEach(([questionId, output]) => {
  //     const v = output?.json_content?.completeness || HighMediumLow.NONE;
  //     qids4completeness[v] = (qids4completeness[v] || []).concat(questionId);
  //   });
  //   const cnt4completeness = {};
  //   Object.keys(qids4completeness).forEach((key) => {
  //     cnt4completeness[key] = qids4completeness[key]?.length;
  //   });
  // confidence
  const qids4confidence = {};
  Object.entries(freshTask?.outputs || {}).forEach(([questionId, output]) => {
    const v = output?.json_content?.confidence || HighMediumLow.NONE; // Treat confidence:undefined as NONE (see ESG-2987)
    qids4confidence[v] = (qids4confidence[v] || []).concat(questionId);
  });
  const cnt4confidence = {};
  Object.keys(qids4confidence).forEach((key) => {
    cnt4confidence[key] = qids4confidence[key]?.length;
  });

  const navigate = useNavigate();

  const pieDialOnClick = (datum) => {
    const qids = {done: answerIds,
		not_done: noAnswerIds,
		error: errorIds
	}[datum.id];
	console.log("pieDialOnClick", datum, qids);
    gotoFilteredQuestionList(navigate, qids, `AI Review: ${datum.id}`);
  };

  const gotoAIReview = () => {
    navigate(routes.aiReview);
  };

  const $RunAIButton = (
    <Flex justify="flex-end">
      <Button variant="outline" onClick={gotoAIReview}>
        Run AI Review <MagicWand color={colors.lime} size={20} />
      </Button>
    </Flex>
  );

  // No task, no panel -- just the button
  if (!freshTask) {
    return $RunAIButton;
  }

  return (
    <>
      {$RunAIButton}
      <Box p={6} border="1px" borderColor="gray.200" borderRadius="lg" bg="white" mb={4}>
        {/* Header Section */}
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <Text color={colors.aiTaskStatus[freshTask?.status]} my={2}>
              {toTitleCase(freshTask?.status)}
            </Text>
            <Heading fontSize="lg" color="gray.800" mt={1}>
              AI Review
            </Heading>
            <Text fontSize="sm" color="gray.500">
              {formatDate(freshTask?.created_date, true, false)}
            </Text>
          </Box>
        </Flex>

        <Divider mb={4} />

        <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={6}>
          {/* Sources */}
          <GridItem>
            <DashboardCard minWidth="200px" maxWidth="500px">
              <H3>Sources</H3>
              {isSourceInfoLoading ? <LocalSpinner /> : <PieDialWithTotal data={sourcesPieData} totalLabel="Sources" onClick={sourcesPieOnClick} />}
            </DashboardCard>
          </GridItem>

          <GridItem>
            <DashboardCard minWidth="200px" maxWidth="500px">
              <H3>Requirements Reviewed</H3>
              <PieDialWithTotal
                data={[
                  { id: "done", value: outputs - errorIds.length, color: colors.aiTaskStatus.done },
                  {
                    id: "not_done",
                    value: inputQuestions - outputs,
                    color: colors.aiTaskStatus[freshTask?.status==="done"? "draft" : freshTask?.status /* task done but some Qs failed? dont show them in green*/],
                    label: "Not done yet",
                  },
                  { id: "error", value: errorIds.length, color: colors.aiTaskStatus.failed },
                ]}
                totalLabel="Questions"
                onClick={pieDialOnClick}
              />
            </DashboardCard>
          </GridItem>
        </Grid>

        <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={6}>
          <GridItem>
            <DashboardCard>
              <DashboardNumber
                title="Answers Generated"
                titlePosition="start"
                value={answerIds.length}
                onClick={() => gotoFilteredQuestionList(navigate, answerIds, "AI Review: Answers Generated")}
                cardStyle={true}
              />
            </DashboardCard>
          </GridItem>
          <GridItem>
            <DashboardCard>
              <DashboardNumber
                title="No Answer"
                titlePosition="start"
                value={noAnswerIds.length}
                onClick={() => gotoFilteredQuestionList(navigate, noAnswerIds, "AI Review: No Answer")}
              />
            </DashboardCard>
          </GridItem>
          <GridItem>
            <DashboardCard>
              <DashboardNumber title="Sources Used" titlePosition="start" value={sourcesUsed?.length || 0} />
            </DashboardCard>
          </GridItem>
        </Grid>

        <WaffleChart cnt4confidence={cnt4confidence} qids4confidence={qids4confidence} />
      </Box>
    </>
  );
}

function WaffleChart({ cnt4confidence, qids4confidence }) {
  // Format confidence labels
  const formatLabel = (key) => {
    if (key === HighMediumLow.NONE) {
      return "No answer";
    }
    return prettyPrintCodeName(key);
  };

  const data = Object.entries(cnt4confidence).map(([key, value]) => ({
    id: key,
    label: formatLabel(key),
    value: value,
  }));
  const total = Object.values(cnt4confidence).reduce((acc, curr) => acc + curr, 0);
  // const columns = Math.ceil(Math.sqrt(2*total));
  // const rows = Math.ceil(total / columns);
  // const height = Math.max(Math.min(400, rows * 20), 50);

  const columns = total < 40 ? total : 40;
  const rows = Math.ceil(total / columns);

  const height = rows * 30;

  const navigate = useNavigate();

  const handleWaffleClick = (datum) => {
    const confidenceLevel = datum.id;
    const questionIds = qids4confidence[confidenceLevel] || [];
    gotoFilteredQuestionList(navigate, questionIds, `Confidence: ${formatLabel(confidenceLevel)}`);
  };

  // Note: ResponsiveWaffle is actually more work and more fragile than just rendering a grid of squares. But maybe there are little benefits built into nivo.

  return (
    // Note: mb={6} creates a bit of necc space that the waffle doesn't consume
    <Flex direction="column" width="100%">
      <H3>Confidence</H3>
      <Flex justifyContent="flex-start" height={`${height + 100}px`}>
        <ResponsiveWaffle
          data={data}
          total={total}
          rows={rows}
          columns={columns}
          padding={1}
          valueFormat=".1f"
          pixelRatio={1}
          margin={{ top: 50 /* space for the legend */ }}
          colors={(d) => colors.highMediumLow[d.id]}
          borderRadius={3}
          borderColor={{
            from: "color",
            modifiers: [["darker", 0.3]],
          }}
          motionStagger={2}
          onClick={handleWaffleClick}
          legends={[
            {
              anchor: "top",
              direction: "row",
              justify: false,
              translateX: 0,
              translateY: -30,
              itemsSpacing: 20,
              itemWidth: 120,
              itemHeight: 20,
              itemDirection: "left-to-right",
              itemOpacity: 1,
              itemTextColor: "#777",
              symbolSize: 20,
              effects: [
                {
                  on: "hover",
                  style: {
                    itemTextColor: "#000",
                    itemBackground: "#f7fafb",
                  },
                },
              ],
              format: (value) => formatLabel(value),
            },
          ]}
          theme={nivo_theme}
        />
      </Flex>
    </Flex>
  );
}

/**
 * HACK put together sources info from a few places. Can be slow
 * @returns {
 *   sources: FileInfo[] from fineinfoService.list,
 *   sourcesProcessed: FileInfo[] subset of sources with aiDate,
 *   sourcesQueued: FileInfo[] subset of sources ,
 *   unanalysed: FileInfo[] subset of sources ,
 * }
 */
async function fetchSourceInfo({ task, aiTasks }) {
  const inputSources = task?.inputs?.sources || {};
  const prefer_fileinfo_ids = inputSources.prefer_fileinfo_ids || [];
  const other_fileinfo_ids = inputSources.other_fileinfo_ids || [];
  const exclude_fileinfo_ids = inputSources.exclude_fileinfo_ids || [];
  let fileinfoIds = [...prefer_fileinfo_ids, ...other_fileinfo_ids].filter((id) => !exclude_fileinfo_ids.includes(id));
  fileinfoIds = [...new Set(fileinfoIds)];

  let sources = [];
  if (fileinfoIds.length) {
    const fileinfosPage = await fileinfoService.list({
      ids: fileinfoIds,
    });
    sources = fileinfosPage.content;
  }
  // filter out KB
  sources = sources.filter((f) => f.folders[0] !== KB_FOLDER);

  let sourcesProcessed = sources.filter((f) => f.aiDate);
  // fails? - do separately to speed up rendering
  // What is in the queue? TODO test this!
  let sourcesQueued = [];
  aiTasks
    ?.filter((t) => t.task_type === "sources_update" && !isTaskClosed(t))
    .forEach((t) => {
      let queuedFileinfoIds = Object.keys(t.inputs?.fileinfo_ids || {});
      sourcesQueued = sourcesQueued.concat(queuedFileinfoIds);
    });
  sourcesQueued = [...new Set(sourcesQueued)].filter((id) => !sourcesProcessed.find((f) => f.id === id));
  console.log("sourcesQueued", sourcesQueued, "from", aiTasks);
  let unanalysed = sources.filter(fi => ! sourcesProcessed.includes(fi) && ! sourcesQueued.includes(fi));
  // Note: sourcesUsed can be done faster, so it is done separately
  const sourceInfo = {
    sources,
    sourcesProcessed,
    sourcesQueued,
	unanalysed
  };
  console.log("sourcesInfo", sourceInfo);
  return sourceInfo;
} // ./fetchSourceInfo

// Note: split out from fetchSourceInfo to speed up rendering
async function fetchSourceInfo_FailedSources(sources) {
  if (!sources) return null;
  const promises = sources?.map((f) => apiGetAISource(f));
  const sourcesFailed = (await Promise.all(promises)).filter((f) => {
    return f?.error;
  });
  return sourcesFailed;
}

/**
 * keys e.g. ["NONE", "LOW", "MEDIUM", "HIGH"]
 */
function BarChart({ statusField, keys, cnt4key, onClick }) {
  if (!keys) keys = Object.keys(cnt4key);
  const data = keys.map((key) => ({
    k: key,
    value: cnt4key[key] || 0,
    color: colors.highMediumLow[key],
  }));
  const labeller = (d) => {
    const label4value = STATUSFIELD_VALUE_LABEL[statusField] || {};
    return label4value[d] || d;
  };

  return (
    <Box width="100%" height="350px">
      <ResponsiveBar
        data={data}
        keys={["value"]}
        indexBy="k"
        margin={{ top: 50, right: 50, bottom: 50, left: 60 }}
        padding={0.3}
        valueScale={{ type: "linear" }}
        enableGridX={false}
        enableGridY={false}
        axisLeft={null}
        axisBottom={{
          format: (d) => labeller(d),
          tickRotation: -15,
        }}
        // label={labeller}
        labelSkipWidth={0}
        labelSkipHeight={1}
        colors={(d) => d.data.color}
        onClick={(click) => onClick && onClick(click.indexValue, click)}
      />
    </Box>
  );
}
